import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  FolderIcon,
  FolderOpenIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

import { Card, CardHeader, CardTitle, CardContent } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Select } from '../../components/common/Select';
import { Badge } from '../../components/common/Badge';
import { Pagination } from '../../components/common/Pagination';
import Modal from '../../components/common/Modal';
import { Checkbox } from '../../components/common/Checkbox';
import { usePermissions } from '../../hooks/usePermissions';
import { useNotification } from '../../contexts/NotificationContext';
import { pimService } from '../../services/pimService';
import type { 
  Category, 
  CategoryFilters,
  CategoryBulkOperation 
} from '../../types/pim';

export const Categories = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const { showNotification } = useNotification();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState<CategoryFilters>({
    page: 1,
    limit: 20,
    sort_by: 'sort_order',
    sort_direction: 'asc'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkAction, setBulkAction] = useState<'activate' | 'deactivate' | 'delete'>('activate');
  const [viewMode, setViewMode] = useState<'list' | 'tree'>('list');
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());

  // Fetch categories
  const { data: categoriesData, isLoading: loadingCategories, error: categoriesError } = useQuery({
    queryKey: ['pim-categories', filters, viewMode],
    queryFn: () => viewMode === 'tree' 
      ? pimService.getCategoryTree(filters.includeProducts)
      : pimService.getCategories(filters),
    keepPreviousData: true,
    retry: false,
    onError: (error: any) => {
      showNotification('Błąd podczas ładowania kategorii. Spróbuj ponownie.', 'error');
    }
  });

  // Auto-expand all categories in tree view
  useEffect(() => {
    if (viewMode === 'tree' && categoriesData) {
      const expandAll = (categories: Category[]): Set<number> => {
        const ids = new Set<number>();
        const traverse = (cats: Category[]) => {
          if (!cats || !Array.isArray(cats)) return;
          cats.forEach(cat => {
            if (cat.children && cat.children.length > 0) {
              ids.add(cat.id);
              traverse(cat.children);
            }
          });
        };
        traverse(categories);
        return ids;
      };
      // Check if categoriesData is an array or has a data property
      const dataArray = Array.isArray(categoriesData)
        ? categoriesData
        : (categoriesData as any)?.data || [];
      setExpandedCategories(expandAll(dataArray as Category[]));
    }
  }, [viewMode, categoriesData]);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => pimService.deleteCategory(id),
    onSuccess: () => {
      showNotification('Kategoria została pomyślnie usunięta', 'success');
      queryClient.invalidateQueries(['pim-categories']);
      setShowDeleteModal(false);
      setCategoryToDelete(null);
    },
    onError: (error: any) => {
      showNotification(error.message || 'Błąd podczas usuwania kategorii', 'error');
    }
  });

  // Bulk operation mutation
  const bulkMutation = useMutation({
    mutationFn: (operation: CategoryBulkOperation) => pimService.categoryBulkOperation(operation),
    onSuccess: (data) => {
      const result = data.data;
      if (result.failed > 0) {
        showNotification(
          `Operacja zakończona. Przetworzono: ${result.processed}, niepowodzenia: ${result.failed}.`,
          'warning'
        );
      } else {
        showNotification(`Pomyślnie przetworzono ${result.processed} kategorii`, 'success');
      }
      queryClient.invalidateQueries(['pim-categories']);
      setSelectedCategories([]);
      setShowBulkModal(false);
    },
    onError: (error: any) => {
      showNotification(error.message || 'Błąd podczas operacji grupowej', 'error');
    }
  });

  const handleSearch = (value: string) => {
    setFilters(prev => ({
      ...prev,
      search: value || undefined,
      page: 1
    }));
  };

  const handleFilterChange = (key: keyof CategoryFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1
    }));
  };

  const handleSort = (sortBy: string) => {
    setFilters(prev => ({
      ...prev,
      sort_by: sortBy,
      sort_direction: prev.sort_by === sortBy && prev.sort_direction === 'asc' ? 'desc' : 'asc',
      page: 1
    }));
  };

  const renderSortableHeader = (label: string, sortKey: string) => {
    const isActive = filters.sort_by === sortKey;
    const isAsc = filters.sort_direction === 'asc';

    return (
      <th
        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
        onClick={() => handleSort(sortKey)}
      >
        <div className="flex items-center gap-1">
          <span>{label}</span>
          {isActive && (
            isAsc ?
              <ChevronUpIcon className="h-4 w-4" /> :
              <ChevronDownIcon className="h-4 w-4" />
          )}
        </div>
      </th>
    );
  };

  const handleSelectCategory = (categoryId: number) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleSelectAll = () => {
    if (viewMode === 'tree' || !categoriesData?.data) return;
    
    const allIds = categoriesData.data.map(c => c.id);
    setSelectedCategories(prev => 
      prev.length === allIds.length ? [] : allIds
    );
  };

  const handleDeleteCategory = (category: Category) => {
    setCategoryToDelete(category);
    setShowDeleteModal(true);
  };

  const handleBulkOperation = () => {
    const action = bulkAction === 'activate' ? 'activate' : 
                   bulkAction === 'deactivate' ? 'deactivate' : 'delete';
    
    bulkMutation.mutate({
      action,
      categoryIds: selectedCategories
    });
  };

  const toggleExpanded = (categoryId: number) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const renderCategoryTree = (categories: Category[], level = 0) => {
    return categories.map((category) => (
      <div key={category.id} className="space-y-1">
        <div 
          className={`flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 ${
            level > 0 ? 'ml-' + (level * 6) : ''
          }`}
        >
          <div className="flex items-center space-x-3">
            {category.children?.length ? (
              <button
                onClick={() => toggleExpanded(category.id)}
                className="p-1 hover:bg-gray-200 rounded"
              >
                <ChevronRightIcon 
                  className={`h-4 w-4 transform transition-transform ${
                    expandedCategories.has(category.id) ? 'rotate-90' : ''
                  }`} 
                />
              </button>
            ) : (
              <div className="w-6" />
            )}
            
            {category.children?.length ? (
              expandedCategories.has(category.id) ? 
                <FolderOpenIcon className="h-5 w-5 text-primary-600" /> :
                <FolderIcon className="h-5 w-5 text-gray-400" />
            ) : (
              <div className="h-5 w-5" />
            )}
            
            <div>
              <div className="flex items-center space-x-2">
                <span className="font-medium text-gray-900">{category.name}</span>
                {category.is_active ? (
                  <Badge variant="success" icon={CheckCircleIcon} size="sm">{t('pim.categories.active')}</Badge>
                ) : (
                  <Badge variant="secondary" icon={XCircleIcon} size="sm">{t('pim.categories.inactive')}</Badge>
                )}
              </div>
              <div className="text-sm text-gray-500">
                Poziom: {category.level} | Pozycja: {category.sort_order}
                {category.products && category.products.length > 0 && (
                  <span> | Produkty: {category.products.length}</span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {permissions.canWriteProducts && (
              <Link to={`/pim/categories/${category.id}/edit`}>
                <Button variant="ghost" size="sm" icon={PencilIcon}>
                  {t('pim.categories.edit')}
                </Button>
              </Link>
            )}
            {permissions.canDeleteProducts && !category.children?.length && (
              <Button
                variant="ghost"
                size="sm"
                icon={TrashIcon}
                onClick={() => handleDeleteCategory(category)}
              >
                {t('pim.categories.delete')}
              </Button>
            )}
          </div>
        </div>

        {category.children && category.children.length > 0 && expandedCategories.has(category.id) && (
          <div className="ml-6">
            {renderCategoryTree(category.children, level + 1)}
          </div>
        )}
      </div>
    ));
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <Badge variant="success" icon={CheckCircleIcon}>Aktywna</Badge>
    ) : (
      <Badge variant="secondary" icon={XCircleIcon}>Nieaktywna</Badge>
    );
  };

  const clearFilters = () => {
    setFilters({
      page: 1,
      limit: 20,
      sort_by: 'sort_order',
      sort_direction: 'asc'
    });
  };

  if (categoriesError) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error loading categories: {(categoriesError as any).message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('pim.categories.title')}</h1>
          <p className="text-gray-600">{t('pim.categories.subtitle')}</p>
        </div>
        <div className="flex space-x-3">
          <div className="flex border rounded-lg">
            <Button
              variant={viewMode === 'list' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              Lista
            </Button>
            <Button
              variant={viewMode === 'tree' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setViewMode('tree')}
            >
              Drzewo
            </Button>
          </div>
          {permissions.canWriteProducts && (
            <Link to="/pim/categories/new">
              <Button icon={PlusIcon}>
                Dodaj kategorię
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Filters */}
      {viewMode === 'list' && (
        <Card>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Input
                  placeholder={t('pim.categories.searchPlaceholder')}
                  value={filters.search || ''}
                  onChange={(e) => handleSearch(e.target.value)}
                  icon={MagnifyingGlassIcon}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  icon={FunnelIcon}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  {t('pim.categories.filters')}
                </Button>
                {(filters.search || filters.includeInactive) && (
                  <Button
                    variant="outline"
                    icon={XMarkIcon}
                    onClick={clearFilters}
                  >
                    {t('pim.categories.clearFilters')}
                  </Button>
                )}
              </div>
            </div>

            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={!!filters.includeInactive}
                    onChange={(e) => handleFilterChange('includeInactive', e.target.checked)}
                    className="h-4 w-4"
                  />
                  {t('pim.categories.showInactive')}
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={!!filters.includeProducts}
                    onChange={(e) => handleFilterChange('includeProducts', e.target.checked)}
                    className="h-4 w-4"
                  />
                  {t('pim.categories.showProducts')}
                </label>
                <Select
                  label="Sortuj według"
                  value={filters.sort_by || 'sort_order'}
                  onChange={(value) => handleFilterChange('sort_by', value)}
                  options={[
                    { value: 'sort_order', label: 'Pozycja' },
                    { value: 'name', label: 'Nazwa' },
                    { value: 'created_at', label: 'Data utworzenia' },
                    { value: 'level', label: 'Poziom' }
                  ]}
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Bulk Actions */}
      {selectedCategories.length > 0 && permissions.canWriteProducts && viewMode === 'list' && (
        <Card>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                {selectedCategories.length} kategorii zaznaczonych
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setBulkAction('activate');
                    setShowBulkModal(true);
                  }}
                >
                  Aktywuj
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setBulkAction('deactivate');
                    setShowBulkModal(true);
                  }}
                >
                  Dezaktywuj
                </Button>
                {permissions.canDeleteProducts && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setBulkAction('delete');
                      setShowBulkModal(true);
                    }}
                  >
                    Usuń
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Categories Content */}
      <Card>
        <CardContent>
          {loadingCategories ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <p className="mt-2 text-gray-600">Ładowanie kategorii...</p>
            </div>
          ) : viewMode === 'tree' ? (
            <div className="space-y-2">
              {categoriesData?.data?.length ? (
                renderCategoryTree(categoriesData.data)
              ) : (
                <div className="text-center py-12">
                  <FolderIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Brak kategorii</h3>
                  <p className="mt-1 text-sm text-gray-500">Rozpocznij od utworzenia pierwszej kategorii.</p>
                  {permissions.canWriteProducts && (
                    <div className="mt-6">
                      <Link to="/pim/categories/new">
                        <Button icon={PlusIcon}>
                          Dodaj kategorię
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <>
              {!categoriesData?.data?.length ? (
                <div className="text-center py-12">
                  <FolderIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Brak kategorii</h3>
                  <p className="mt-1 text-sm text-gray-500">Rozpocznij od utworzenia pierwszej kategorii.</p>
                  {permissions.canWriteProducts && (
                    <div className="mt-6">
                      <Link to="/pim/categories/new">
                        <Button icon={PlusIcon}>
                          Dodaj kategorię
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {permissions.canWriteProducts && (
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <Checkbox
                              checked={selectedCategories.length === categoriesData.data.length}
                              indeterminate={selectedCategories.length > 0 && selectedCategories.length < categoriesData.data.length}
                              onChange={handleSelectAll}
                            />
                          </th>
                        )}
                        {renderSortableHeader('Nazwa', 'name')}
                        {renderSortableHeader('Poziom', 'level')}
                        {renderSortableHeader('Pozycja', 'sort_order')}
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort('products_count')}
                        >
                          <div className="flex items-center gap-1">
                            <span>Produkty</span>
                            {filters.sort_by === 'products_count' && (
                              filters.sort_direction === 'asc' ?
                                <ChevronUpIcon className="h-4 w-4" /> :
                                <ChevronDownIcon className="h-4 w-4" />
                            )}
                          </div>
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Akcje
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {categoriesData.data.map((category) => (
                        <tr key={category.id} className="hover:bg-gray-50">
                          {permissions.canWriteProducts && (
                            <td className="px-6 py-4 whitespace-nowrap">
                              <Checkbox
                                checked={selectedCategories.includes(category.id)}
                                onChange={() => handleSelectCategory(category.id)}
                              />
                            </td>
                          )}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="text-sm font-medium text-gray-900">{category.name}</div>
                            </div>
                            {category.description && (
                              <div className="text-sm text-gray-500 truncate max-w-xs">
                                {category.description}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex items-center gap-1">
                              <span className={`${category.level === 0 ? 'font-medium text-gray-900' : 'text-gray-600'}`}>
                                {category.level}
                              </span>
                              {category.level === 0 && (
                                <span className="text-xs text-gray-400">(główna)</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {category.sort_order}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(category.is_active)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex items-center gap-1">
                              <span className={`${!category.products?.length ? 'text-gray-400' : 'text-gray-900 font-medium'}`}>
                                {category.products?.length || 0}
                              </span>
                              {!category.products?.length && (
                                <span className="text-xs text-gray-400">brak</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex justify-end gap-2">
                              {permissions.canWriteProducts && (
                                <Link to={`/pim/categories/${category.id}/edit`}>
                                  <Button variant="ghost" size="sm" icon={PencilIcon}>
                                    Edytuj
                                  </Button>
                                </Link>
                              )}
                              {permissions.canDeleteProducts && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  icon={TrashIcon}
                                  onClick={() => handleDeleteCategory(category)}
                                >
                                  Usuń
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {categoriesData?.pagination && (
                <div className="mt-6">
                  <Pagination
                    currentPage={categoriesData.pagination.page}
                    totalPages={categoriesData.pagination.totalPages}
                    totalItems={categoriesData.pagination.total}
                    itemsPerPage={categoriesData.pagination.limit}
                    onPageChange={(page) => handleFilterChange('page', page)}
                  />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={t('pim.categories.deleteCategory')}
      >
        <div className="space-y-4">
          <p>
            Czy na pewno chcesz usunąć kategorię "{categoryToDelete?.name}"?
            Ta operacja jest nieodwracalna.
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => categoryToDelete && deleteMutation.mutate(categoryToDelete.id)}
              disabled={deleteMutation.isLoading}
            >
              {deleteMutation.isLoading ? t('pim.categories.deleting') : t('pim.categories.delete')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Bulk Action Modal */}
      <Modal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        title={`Operacja grupowa: ${bulkAction}`}
      >
        <div className="space-y-4">
          <p>
            Czy na pewno chcesz {bulkAction === 'activate' ? 'aktywować' : 
                                 bulkAction === 'deactivate' ? 'dezaktywować' : 'usunąć'} {selectedCategories.length} zaznaczonych kategorii?
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setShowBulkModal(false)}
            >
              Anuluj
            </Button>
            <Button
              variant={bulkAction === 'delete' ? 'danger' : 'primary'}
              onClick={handleBulkOperation}
              disabled={bulkMutation.isLoading}
            >
              {bulkMutation.isLoading ? 'Przetwarzanie...' : 'Potwierdź'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};